import {
  Check,
  Crown,
  LoaderCircle,
  RefreshCcw,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  billingErrorRecovery,
  canPerformBillingAction,
  changeSubscriptionPlan,
  createBillingIdempotencyKey,
  createBillingIdempotencyRegistry,
  createSubscriptionCheckout,
  planEligibleForWorkspace,
  previewSubscriptionPlanChange,
  readBillingCatalog,
  readWorkspaceBilling,
  validatePlanChangePreview,
  type BillingCatalog,
  type BillingCatalogPlan,
  type BillingWorkspaceType,
  type PlanChangePreview,
  type WorkspaceBillingState,
} from "@/lib/billing";

export type WalletSubscriptionTab = "Individual" | "Pro";

const SUBSCRIPTION_TABS: WalletSubscriptionTab[] = ["Individual", "Pro"];
const INDIVIDUAL_PLAN_CODES = new Set(["personal_starter", "personal_intermediate"]);
const PRO_PLAN_CODES = new Set(["personal_pro"]);

export function walletSubscriptionTabForPlanCode(
  planCode: string,
): WalletSubscriptionTab | null {
  const code = String(planCode || "").trim().toLowerCase();
  if (INDIVIDUAL_PLAN_CODES.has(code)) return "Individual";
  if (PRO_PLAN_CODES.has(code)) return "Pro";
  return null;
}

export function walletSubscriptionPlansForTab(
  plans: BillingCatalogPlan[],
  tab: WalletSubscriptionTab,
) {
  return plans
    .filter((plan) => walletSubscriptionTabForPlanCode(plan.code) === tab)
    .sort((left, right) => left.monthly_price_minor - right.monthly_price_minor);
}

export function WalletSubscriptionChooser({
  workspaceId,
  workspaceName,
  workspaceType,
  canManageBilling,
}: {
  workspaceId: string;
  workspaceName: string;
  workspaceType: BillingWorkspaceType;
  canManageBilling: boolean;
}) {
  const [tab, setTab] = useState<WalletSubscriptionTab>("Individual");
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [billing, setBilling] = useState<WorkspaceBillingState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<BillingCatalogPlan | null>(null);
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionNotice, setActionNotice] = useState("");
  const loadGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const actionGeneration = useRef(0);
  const previewController = useRef<AbortController | null>(null);
  const currentWorkspaceId = useRef(workspaceId);
  const idempotencyRegistry = useRef(
    createBillingIdempotencyRegistry(createBillingIdempotencyKey),
  );
  const normalizedWorkspaceId = workspaceId.trim();
  currentWorkspaceId.current = normalizedWorkspaceId;

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      const selectedWorkspaceId = normalizedWorkspaceId;
      const generation = ++loadGeneration.current;
      previewController.current?.abort();
      previewGeneration.current += 1;
      setSelectedPlan(null);
      setPreview(null);
      setPreviewing(false);
      setActionError("");
      setLoading(true);
      setLoadError("");
      setActionNotice("");

      if (!selectedWorkspaceId) {
        setCatalog(null);
        setBilling(null);
        setLoading(false);
        setLoadError("Select a workspace before choosing a subscription.");
        return;
      }

      try {
        const [nextCatalog, nextBilling] = await Promise.all([
          readBillingCatalog(selectedWorkspaceId, signal),
          readWorkspaceBilling(selectedWorkspaceId, signal),
        ]);
        if (
          signal?.aborted ||
          generation !== loadGeneration.current ||
          currentWorkspaceId.current !== selectedWorkspaceId
        ) {
          return;
        }
        if (
          nextCatalog.workspace_id !== selectedWorkspaceId ||
          nextBilling.workspace_id !== selectedWorkspaceId ||
          nextCatalog.workspace_type !== nextBilling.workspace_type
        ) {
          throw new Error("Billing details did not match the selected workspace.");
        }
        setCatalog(nextCatalog);
        setBilling(nextBilling);
        setTab(walletSubscriptionTabForPlanCode(nextBilling.plan.code) || "Individual");
      } catch (error) {
        if (
          signal?.aborted ||
          generation !== loadGeneration.current ||
          currentWorkspaceId.current !== selectedWorkspaceId
        ) {
          return;
        }
        setCatalog(null);
        setBilling(null);
        setLoadError(billingErrorRecovery(error).title);
      } finally {
        if (generation === loadGeneration.current) setLoading(false);
      }
    },
    [normalizedWorkspaceId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setSelectedPlan(null);
    setPreview(null);
    setPreviewing(false);
    setActionBusy(false);
    setActionError("");
    setActionNotice("");
    idempotencyRegistry.current.clearAll();
    previewController.current?.abort();
    void refresh(controller.signal);
    return () => {
      controller.abort();
      previewController.current?.abort();
      loadGeneration.current += 1;
      previewGeneration.current += 1;
      actionGeneration.current += 1;
    };
  }, [refresh]);

  const authoritativeWorkspaceType = billing?.workspace_type || workspaceType;
  const plans = useMemo(
    () => walletSubscriptionPlansForTab(catalog?.plans || [], tab),
    [catalog?.plans, tab],
  );
  const effectiveCanManage = Boolean(canManageBilling && billing?.can_manage_billing);
  const currentPlanCode = billing?.plan.code || "";
  const hasPaidPlan = Boolean(currentPlanCode && currentPlanCode !== "free");
  const canCheckout = effectiveCanManage && canPerformBillingAction(billing, "checkout");
  const canChangePlan = effectiveCanManage && canPerformBillingAction(billing, "change_plan");
  const needsPreview = Boolean(selectedPlan && hasPaidPlan);

  const closeConfirmation = () => {
    previewController.current?.abort();
    previewGeneration.current += 1;
    setSelectedPlan(null);
    setPreview(null);
    setPreviewing(false);
    setActionError("");
  };

  const choosePlan = async (plan: BillingCatalogPlan) => {
    setActionError("");
    setActionNotice("");
    if (
      !billing ||
      !catalog ||
      plan.code === currentPlanCode ||
      !planEligibleForWorkspace(plan, authoritativeWorkspaceType)
    ) {
      return;
    }
    if (hasPaidPlan ? !canChangePlan : !canCheckout) {
      setActionError("A workspace billing admin must make this subscription change.");
      return;
    }

    previewController.current?.abort();
    const generation = ++previewGeneration.current;
    setSelectedPlan(plan);
    setPreview(null);

    if (!hasPaidPlan) {
      setPreviewing(false);
      return;
    }

    const termsVersion = plan.terms_version || catalog.terms_version;
    if (!termsVersion) {
      setActionError("Current plan terms are unavailable. Refresh subscriptions and try again.");
      return;
    }

    const controller = new AbortController();
    previewController.current = controller;
    setPreviewing(true);
    try {
      const nextPreview = await previewSubscriptionPlanChange(
        normalizedWorkspaceId,
        {
          plan_code: plan.code,
          expected_terms_version: termsVersion,
        },
        controller.signal,
      );
      if (
        controller.signal.aborted ||
        generation !== previewGeneration.current ||
        currentWorkspaceId.current !== normalizedWorkspaceId
      ) {
        return;
      }
      setPreview(
        validatePlanChangePreview(nextPreview, {
          workspaceId: normalizedWorkspaceId,
          currentPlanCode,
          targetPlanCode: plan.code,
          termsVersion,
        }),
      );
    } catch (error) {
      if (
        controller.signal.aborted ||
        generation !== previewGeneration.current ||
        currentWorkspaceId.current !== normalizedWorkspaceId
      ) {
        return;
      }
      setActionError(billingErrorRecovery(error).title);
    } finally {
      if (generation === previewGeneration.current) setPreviewing(false);
    }
  };

  const confirmPlan = async () => {
    if (!selectedPlan || !catalog || !billing || actionBusy) return;
    const selectedWorkspaceId = normalizedWorkspaceId;
    const termsVersion = selectedPlan.terms_version || catalog.terms_version;
    if (!selectedWorkspaceId || !termsVersion) {
      setActionError("Current plan terms are unavailable. Refresh subscriptions and try again.");
      return;
    }
    if (hasPaidPlan && !preview) {
      setActionError("Review the authoritative plan-change preview before confirming.");
      return;
    }
    if (hasPaidPlan) {
      try {
        validatePlanChangePreview(preview!, {
          workspaceId: selectedWorkspaceId,
          currentPlanCode,
          targetPlanCode: selectedPlan.code,
          termsVersion,
        });
      } catch (error) {
        setActionError(error instanceof Error ? error.message : "Refresh the plan preview.");
        return;
      }
    }

    const generation = ++actionGeneration.current;
    const planCode = selectedPlan.code;
    const intent = [
      "wallet-subscription",
      selectedWorkspaceId,
      currentPlanCode || "free",
      planCode,
      termsVersion,
    ].join(":");
    setActionBusy(true);
    setActionError("");
    setActionNotice("");

    try {
      const input = {
        plan_code: planCode,
        expected_terms_version: termsVersion,
        return_path: `/settings?section=billing&workspace_id=${encodeURIComponent(selectedWorkspaceId)}`,
      };
      const response = hasPaidPlan
        ? await changeSubscriptionPlan(
            selectedWorkspaceId,
            input,
            idempotencyRegistry.current.keyFor(intent),
          )
        : await createSubscriptionCheckout(
            selectedWorkspaceId,
            input,
            idempotencyRegistry.current.keyFor(intent),
          );
      if (
        generation !== actionGeneration.current ||
        currentWorkspaceId.current !== selectedWorkspaceId
      ) {
        return;
      }
      if (response.workspace_id !== selectedWorkspaceId) {
        throw new Error("The checkout response did not match the selected workspace.");
      }
      if (response.status === "failed" || response.status === "expired") {
        idempotencyRegistry.current.clear(intent);
        throw new Error("Subscription checkout could not be started.");
      }

      const redirect = response.redirect_url || response.checkout_url;
      if (redirect && typeof window !== "undefined") {
        window.location.assign(redirect);
        return;
      }
      if (!response.operation_id) {
        throw new Error("Checkout started without a confirmation reference. Try again.");
      }
      setActionNotice(
        "Your subscription change is being confirmed. Open full billing to review its status.",
      );
      closeConfirmation();
    } catch (error) {
      if (
        generation !== actionGeneration.current ||
        currentWorkspaceId.current !== selectedWorkspaceId
      ) {
        return;
      }
      const recovery = billingErrorRecovery(error);
      setActionError(recovery.title);
      if (recovery.resetIntent || !recovery.retryable) {
        idempotencyRegistry.current.clear(intent);
      }
    } finally {
      if (
        generation === actionGeneration.current &&
        currentWorkspaceId.current === selectedWorkspaceId
      ) {
        setActionBusy(false);
      }
    }
  };

  return (
    <section aria-busy={loading} data-testid="wallet-subscription-chooser">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/40">
            {workspaceName || "Selected workspace"}
          </p>
          <h3 className="mt-1 text-[20px] font-semibold tracking-[-0.03em]">
            Choose your subscription
          </h3>
          <p className="mt-1 max-w-xl text-[12px] leading-5 text-black/45">
            Compare personal subscriptions and continue through secure checkout.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading || actionBusy}
          className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-black/10 bg-white/30 px-3 text-[11px] font-semibold text-black/55 disabled:opacity-45"
          aria-label="Refresh subscriptions"
        >
          <RefreshCcw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
          Refresh
        </button>
      </div>

      <div
        className="mb-5 grid grid-cols-2 rounded-2xl p-1"
        style={{ background: "rgba(0,0,0,0.045)" }}
        role="tablist"
        aria-label="Subscription type"
      >
        {SUBSCRIPTION_TABS.map((item) => {
          const active = tab === item;
          const Icon = item === "Individual" ? UserRound : Crown;
          return (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`wallet-subscription-${item.toLowerCase()}`}
              id={`wallet-subscription-tab-${item.toLowerCase()}`}
              disabled={actionBusy}
              onClick={() => {
                closeConfirmation();
                setActionNotice("");
                setTab(item);
              }}
              className="flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-[13px] font-semibold transition-colors disabled:opacity-45"
              style={{
                background: active ? "rgba(255,255,255,0.9)" : "transparent",
                boxShadow: active ? "0 1px 5px rgba(0,0,0,0.08)" : "none",
                color: active ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0.4)",
              }}
            >
              <Icon size={14} />
              {item}
            </button>
          );
        })}
      </div>

      {loadError ? (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-50/55 p-4" role="alert">
          <p className="text-[13px] font-semibold">Subscriptions unavailable</p>
          <p className="mt-1 text-[12px] leading-5 text-black/50">{loadError}</p>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="mt-3 min-h-10 rounded-xl border border-black/10 px-3 text-[11px] font-semibold disabled:opacity-45"
          >
            Try again
          </button>
        </div>
      ) : loading && !billing ? (
        <div className="flex min-h-44 items-center justify-center rounded-2xl border border-white/60 bg-white/20">
          <LoaderCircle className="h-5 w-5 animate-spin text-black/40" aria-label="Loading plans" />
        </div>
      ) : billing && catalog ? (
        <div
          role="tabpanel"
          id={`wallet-subscription-${tab.toLowerCase()}`}
          aria-labelledby={`wallet-subscription-tab-${tab.toLowerCase()}`}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/65 bg-white/25 px-4 py-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-black/35">
                Current subscription
              </p>
              <p className="mt-1 text-[14px] font-semibold">{billing.plan.name || "Free"}</p>
            </div>
            <span className="rounded-full bg-black/[0.06] px-2.5 py-1 text-[10px] font-semibold capitalize text-black/50">
              {billing.status.replaceAll("_", " ")}
            </span>
          </div>

          {actionNotice ? (
            <p
              className="mb-4 rounded-2xl border border-emerald-500/25 bg-emerald-50/50 p-3 text-[12px] leading-5 text-emerald-800"
              role="status"
            >
              {actionNotice}
            </p>
          ) : null}

          {!effectiveCanManage ? (
            <p className="mb-4 flex items-start gap-2 rounded-2xl border border-black/10 bg-white/20 p-3 text-[11px] leading-5 text-black/50">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              {billing.billing_permission_reason ||
                "Ask a workspace billing admin to choose or change this subscription."}
            </p>
          ) : null}

          {authoritativeWorkspaceType !== "personal" ? (
            <div className="rounded-2xl border border-black/10 bg-white/25 p-5">
              <p className="text-[14px] font-semibold">
                Individual and Pro are personal workspace plans
              </p>
              <p className="mt-2 text-[12px] leading-5 text-black/50">
                This is an organization workspace. Use full billing to manage Enterprise or agent
                subscriptions.
              </p>
            </div>
          ) : plans.length ? (
            <div className={`grid gap-3 ${plans.length > 1 ? "sm:grid-cols-2" : ""}`}>
              {plans.map((plan) => {
                const current = plan.code === currentPlanCode;
                const eligible = planEligibleForWorkspace(plan, authoritativeWorkspaceType);
                const selectable =
                  !current &&
                  eligible &&
                  effectiveCanManage &&
                  (hasPaidPlan ? canChangePlan : canCheckout);
                const isSelected = selectedPlan?.code === plan.code;
                const reason = !eligible
                  ? plan.ineligibility_reason || "This plan is not available for this workspace."
                  : !effectiveCanManage
                    ? "Billing admin access is required."
                    : hasPaidPlan && !canChangePlan
                      ? "This subscription cannot be changed in its current state."
                      : !hasPaidPlan && !canCheckout
                        ? "Checkout is not available in the current billing state."
                        : "";
                return (
                  <article
                    key={plan.code}
                    className="flex min-h-[250px] flex-col rounded-2xl border p-5"
                    style={{
                      background: isSelected ? "rgba(255,255,255,0.62)" : "rgba(255,255,255,0.25)",
                      borderColor: isSelected ? "rgba(0,0,0,0.22)" : "rgba(255,255,255,0.7)",
                    }}
                    data-testid={`wallet-subscription-plan-${plan.code}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-black/35">
                          {tab}
                        </p>
                        <h4 className="mt-2 text-[18px] font-semibold">{plan.name}</h4>
                      </div>
                      {current ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.07] px-2 py-1 text-[9px] font-semibold uppercase text-black/55">
                          <Check size={10} /> Current
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-4 text-[22px] font-semibold tracking-[-0.04em]">
                      {plan.price_display}
                    </p>
                    {plan.included_compute_display ? (
                      <p className="mt-1 text-[11px] font-semibold text-black/55">
                        {plan.included_compute_display} compute included
                      </p>
                    ) : null}
                    <p className="mt-3 flex-1 text-[11px] leading-5 text-black/45">
                      {plan.description ||
                        "A monthly compute allowance for this personal workspace."}
                    </p>
                    {reason ? (
                      <p className="mb-3 text-[10px] leading-4 text-black/40">{reason}</p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void choosePlan(plan)}
                      disabled={!selectable || previewing || actionBusy}
                      className="min-h-11 w-full rounded-xl bg-[rgba(14,12,8,0.9)] px-3 text-[11px] font-semibold text-white disabled:bg-black/[0.06] disabled:text-black/35"
                      aria-label={
                        current
                          ? `${plan.name} is your current subscription`
                          : `Choose ${plan.name} subscription`
                      }
                    >
                      {current ? "Current plan" : isSelected ? "Reviewing plan" : `Choose ${plan.name}`}
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-black/10 bg-white/25 p-5">
              <p className="text-[14px] font-semibold">No {tab.toLowerCase()} plans available</p>
              <p className="mt-2 text-[12px] leading-5 text-black/50">
                The live billing catalogue does not currently include plans for this tab.
              </p>
            </div>
          )}

          {selectedPlan ? (
            <div
              className="mt-4 rounded-2xl border border-black/15 bg-white/65 p-5"
              data-testid={`wallet-subscription-confirm-${selectedPlan.code}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-black/35">
                    Confirm subscription
                  </p>
                  <h4 className="mt-1 text-[16px] font-semibold">{selectedPlan.name}</h4>
                  <p className="mt-1 text-[12px] text-black/50">{selectedPlan.price_display}</p>
                </div>
                {needsPreview && preview ? (
                  <span className="rounded-full bg-black/[0.06] px-2.5 py-1 text-[9px] font-semibold uppercase text-black/50">
                    Server preview ready
                  </span>
                ) : null}
              </div>

              {previewing ? (
                <p className="mt-4 flex items-center gap-2 text-[12px] text-black/50" role="status">
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                  Loading authoritative plan-change preview…
                </p>
              ) : preview ? (
                <div className="mt-4 grid gap-2 rounded-xl bg-black/[0.035] p-3 sm:grid-cols-2">
                  <PlanDetail
                    label={preview.timing === "next_period" ? "Due now" : "Prorated due now"}
                    value={preview.amount_due_display}
                  />
                  <PlanDetail label="Effective" value={formatPreviewDate(preview.effective_at)} />
                </div>
              ) : (
                <p className="mt-4 text-[11px] leading-5 text-black/45">
                  You will continue to secure checkout before the subscription changes.
                </p>
              )}

              {actionError ? (
                <p className="mt-3 text-[11px] leading-5 text-amber-700" role="alert">
                  {actionError}
                </p>
              ) : null}

              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeConfirmation}
                  disabled={actionBusy}
                  className="min-h-11 rounded-xl border border-black/10 px-4 text-[11px] font-semibold disabled:opacity-45"
                >
                  Back to plans
                </button>
                <button
                  type="button"
                  onClick={() => void confirmPlan()}
                  disabled={actionBusy || previewing || (needsPreview && !preview)}
                  className="min-h-11 rounded-xl bg-[rgba(14,12,8,0.9)] px-5 text-[11px] font-semibold text-white disabled:opacity-45"
                >
                  {actionBusy ? "Starting checkout" : `Confirm ${selectedPlan.name}`}
                </button>
              </div>
            </div>
          ) : actionError ? (
            <p className="mt-4 text-[11px] leading-5 text-amber-700" role="alert">
              {actionError}
            </p>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-black/[0.06] pt-4">
            <p className="text-[10px] leading-4 text-black/40">
              Enterprise, agent subscriptions, invoices, and payment methods remain in full
              billing.
            </p>
            <a
              href={`/settings?section=billing&workspace_id=${encodeURIComponent(normalizedWorkspaceId)}`}
              className="inline-flex min-h-10 items-center rounded-xl border border-black/10 bg-white/25 px-3 text-[11px] font-semibold text-black/60"
            >
              Open full billing
            </a>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function PlanDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-black/35">{label}</p>
      <p className="mt-1 text-[12px] font-semibold">{value || "—"}</p>
    </div>
  );
}

function formatPreviewDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}
